import { supabase } from '../lib/supabase';

/**
 * Notifications Service - Handles all notification-related operations with Supabase
 * Includes real-time subscriptions, CRUD operations, and preference management
 */

export const notificationsService = {
  /**
   * Get all notifications for the current user with pagination and filtering
   */
  async getAll(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('notifications')?.select(`
        *,
        related_job:jobs(id, title, company_id),
        related_company:companies(id, name, logo_url),
        related_application:job_applications(id, status)
      `)?.eq('user_id', user?.id);

    // Apply filters
    if (filters?.type && filters?.type !== 'all') {
      query = query?.eq('type', filters?.type);
    }

    if (filters?.isRead !== undefined) {
      query = query?.eq('is_read', filters?.isRead);
    }

    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }

    if (filters?.dateFrom) {
      query = query?.gte('created_at', filters?.dateFrom);
    }

    if (filters?.dateTo) {
      query = query?.lte('created_at', filters?.dateTo);
    }

    // Apply sorting
    const sortBy = filters?.sortBy || 'created_at';
    const sortOrder = filters?.sortOrder || 'desc';
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    if (filters?.offset) {
      query = query?.range(filters?.offset, filters?.offset + (filters?.limit || 20) - 1);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(notif => ({
      id: notif?.id,
      userId: notif?.user_id,
      type: notif?.type,
      priority: notif?.priority,
      title: notif?.title,
      message: notif?.message,
      actionUrl: notif?.action_url,
      actionLabel: notif?.action_label,
      metadata: notif?.metadata,
      isRead: notif?.is_read,
      readAt: notif?.read_at,
      createdAt: notif?.created_at,
      expiresAt: notif?.expires_at,
      relatedJob: notif?.related_job ? {
        id: notif?.related_job?.id,
        title: notif?.related_job?.title,
        companyId: notif?.related_job?.company_id
      } : null,
      relatedCompany: notif?.related_company ? {
        id: notif?.related_company?.id,
        name: notif?.related_company?.name,
        logoUrl: notif?.related_company?.logo_url
      } : null,
      relatedApplication: notif?.related_application ? {
        id: notif?.related_application?.id,
        status: notif?.related_application?.status
      } : null
    }));
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.rpc('get_unread_notification_count', {
      p_user_id: user?.id
    });

    if (error) throw error;
    return data || 0;
  },

  /**
   * Mark single notification as read
   */
  async markAsRead(notificationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('id', notificationId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isRead: data?.is_read,
      readAt: data?.read_at
    };
  },

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds) {
    const { data, error } = await supabase?.rpc('mark_notifications_as_read', {
      notification_ids: notificationIds
    });

    if (error) throw error;
    return data; // Returns count of updated notifications
  },

  /**
   * Archive (delete) single notification
   */
  async archive(notificationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId)?.eq('user_id', user?.id);

    if (error) throw error;
    return true;
  },

  /**
   * Archive multiple notifications
   */
  async archiveMultiple(notificationIds) {
    const { data, error } = await supabase?.rpc('archive_notifications', {
      notification_ids: notificationIds
    });

    if (error) throw error;
    return data; // Returns count of deleted notifications
  },

  /**
   * Get notification preferences
   */
  async getPreferences() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', user?.id);

    if (error) throw error;

    return data?.map(pref => ({
      id: pref?.id,
      userId: pref?.user_id,
      notificationType: pref?.notification_type,
      emailEnabled: pref?.email_enabled,
      pushEnabled: pref?.push_enabled,
      inAppEnabled: pref?.in_app_enabled,
      frequency: pref?.frequency,
      quietHoursStart: pref?.quiet_hours_start,
      quietHoursEnd: pref?.quiet_hours_end,
      createdAt: pref?.created_at,
      updatedAt: pref?.updated_at
    }));
  },

  /**
   * Update notification preferences
   */
  async updatePreferences(notificationType, preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notification_preferences')?.upsert({
        user_id: user?.id,
        notification_type: notificationType,
        email_enabled: preferences?.emailEnabled,
        push_enabled: preferences?.pushEnabled,
        in_app_enabled: preferences?.inAppEnabled,
        frequency: preferences?.frequency,
        quiet_hours_start: preferences?.quietHoursStart,
        quiet_hours_end: preferences?.quietHoursEnd,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'user_id,notification_type'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      notificationType: data?.notification_type,
      emailEnabled: data?.email_enabled,
      pushEnabled: data?.push_enabled,
      inAppEnabled: data?.in_app_enabled,
      frequency: data?.frequency,
      quietHoursStart: data?.quiet_hours_start,
      quietHoursEnd: data?.quiet_hours_end
    };
  },

  /**
   * Get notification groups (consolidated notifications)
   */
  async getGroups() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notification_groups')?.select('*')?.eq('user_id', user?.id)?.order('last_notification_at', { ascending: false });

    if (error) throw error;

    return data?.map(group => ({
      id: group?.id,
      userId: group?.user_id,
      groupKey: group?.group_key,
      groupTitle: group?.group_title,
      notificationCount: group?.notification_count,
      lastNotificationAt: group?.last_notification_at,
      isRead: group?.is_read,
      createdAt: group?.created_at
    }));
  },

  /**
   * Subscribe to real-time notification updates
   */
  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel('notifications-changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Convert payload to camelCase and call callback
          const eventType = payload?.eventType;
          const notification = payload?.new || payload?.old;

          if (notification) {
            callback({
              eventType,
              notification: {
                id: notification?.id,
                userId: notification?.user_id,
                type: notification?.type,
                priority: notification?.priority,
                title: notification?.title,
                message: notification?.message,
                actionUrl: notification?.action_url,
                actionLabel: notification?.action_label,
                metadata: notification?.metadata,
                isRead: notification?.is_read,
                readAt: notification?.read_at,
                createdAt: notification?.created_at,
                expiresAt: notification?.expires_at
              }
            });
          }
        }
      )?.subscribe();

    // Return unsubscribe function
    return () => {
      supabase?.removeChannel(channel);
    };
  },

  /**
   * Create a new notification (for testing/admin purposes)
   */
  async create(notificationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notifications')?.insert({
        user_id: user?.id,
        type: notificationData?.type,
        priority: notificationData?.priority || 'medium',
        title: notificationData?.title,
        message: notificationData?.message,
        action_url: notificationData?.actionUrl,
        action_label: notificationData?.actionLabel,
        metadata: notificationData?.metadata || {},
        related_application_id: notificationData?.relatedApplicationId,
        related_job_id: notificationData?.relatedJobId,
        related_company_id: notificationData?.relatedCompanyId,
        expires_at: notificationData?.expiresAt
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      type: data?.type,
      priority: data?.priority,
      title: data?.title,
      message: data?.message,
      createdAt: data?.created_at
    };
  }
};